import { get, set } from 'lodash';
import { defineComponent, PropType } from 'vue';
import { FormApi } from 'final-form';

import { MediaTransferHelper } from '@skinsoft/media-transfer-helper';
import { reactiveVueMixin } from '@skinsoft/reactive-vue';

import { PRINCIPAL_IMAGE_CLICK_TOPIC, DELETE_IMAGE_CLICK_TOPIC, IMAGE_UPLOADED_TOPIC, MEDIA_ID_CHANGED_FROM_CACHE } from '@skinsoft/bus/src/busTopics';
import { MediaArea } from '../medias-area';
import _ from 'lodash';
import { MediaIdChangedFromCacheEvent } from '@skinsoft/ui/src/item-card/media';

export interface AddMediaEvent {
    cacheKey: string,
    newMediaId?: string
}

export const MediasFieldsArea = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sMediaArea: MediaArea
    },
    props: {
        form: { type: Object as PropType<FormApi<any>> },
        fieldName: {type: String},
        // entity is passed generically through record-mask-display, shared across heterogeneous entity types (item, audit, condition report, ...)
        entity: { type: Object as PropType<any> },
        canUpload: {
            type: Boolean,
            default: true,
        },
        canDelete: {
            type: Boolean,
            default: true,
        },
        canSetFirstMedia: {
            type: Boolean,
            default: true,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        forceUploadInCache: {
            type: Boolean,
            default: false,
        },
    },
    template: require('./medias-fields-area.html'),
    data() {
        return {
            mediaUploadInProgress: false,

            DELETE_IMAGE_CLICK_TOPIC,
            IMAGE_UPLOADED_TOPIC,
            PRINCIPAL_IMAGE_CLICK_TOPIC,
            MEDIA_ID_CHANGED_FROM_CACHE,
        }
    },
    computed: {
        associatedMediasFieldName(): string {
            return `${this.fieldName}.medias`;
        },
        firstMediaFieldName(): string {
            return `${this.fieldName}.first`;
        },
        reportMedias() {
            let medias: any[] = this.form ? get(this.form.getState().values, this.associatedMediasFieldName ) : get(this.entity, this.associatedMediasFieldName);

            if (!medias) {
                medias = [];
                if(this.form){
                    set(this.form.getState().values, this.associatedMediasFieldName, medias)
                }else{
                    set(this.entity, this.associatedMediasFieldName, medias)
                }
                return medias;
            }

            return medias
        },
    },
    methods: {
        onImageDeleteClick(event) {
            const medias = this.form ? get(this.form.getState().values, this.associatedMediasFieldName ) : get(this.entity, this.associatedMediasFieldName);
            if (medias) {
                const removedMediaId = medias.splice(event.imageListIndex, 1)
                const firstMedia = this.form ? get(this.form.getState().values, this.firstMediaFieldName ) : get(this.entity, this.firstMediaFieldName);
                if (removedMediaId === firstMedia) {
                    this.modifyFirstMedia(null)
                }
            }
            this.$emit(DELETE_IMAGE_CLICK_TOPIC, event)
        },
        onPrincipalImageClick(mediaId) {
            this.modifyFirstMedia(mediaId)
            this.$emit('imageChoice', mediaId)
        },
        onMediaIdChangedFromCache(mediaIds: MediaIdChangedFromCacheEvent) {
            this.updateMediaFromCacheChange(mediaIds.oldMediaId, mediaIds.newMediaId)
        },
        modifyFirstMedia(mediaId) {
            if(this.form){
                set(this.form.getState().values, this.firstMediaFieldName, mediaId)
            }else{
                _.set(this.entity, this.firstMediaFieldName, mediaId)
            }
        },
        updateMediaFromCacheChange(oldMediaId, newMediaId) {
            let medias = this.form ? get(this.form.getState().values, this.associatedMediasFieldName ) : get(this.entity, this.associatedMediasFieldName);
            const index = medias.indexOf(oldMediaId)
            medias.splice(index, 1)
            medias.push(newMediaId)

            const firstMedia = this.form ? get(this.form.getState().values, this.firstMediaFieldName ) : get(this.entity, this.firstMediaFieldName);
            if (firstMedia === oldMediaId) {
                this.modifyFirstMedia(newMediaId)
            }

            // save report to keep modifications
            if(this.form){
                this.form.submit();
            }
        },
        async uploadMedia(fileData) {
            const formData = new FormData()
            formData.append("file", fileData.file)

            let medias: any[] = this.form ? get(this.form.getState().values, this.associatedMediasFieldName ) : get(this.entity, this.associatedMediasFieldName)
            if (!medias) {
                medias = []
            }
            this.mediaUploadInProgress = true
            // Retrieving temp id and uploading the media on backend asynchronously
            let cacheKey = ''
            if(window.navigator.onLine && !this.forceUploadInCache) {
                cacheKey = this.$imageTransferHelper.uploadImage(fileData.file.type, fileData.file, fileData.url).cacheKey
            }
            else {
                cacheKey = this.$imageTransferHelper.uploadImageInCache(fileData.file, fileData.url)
            }
            setTimeout(() => { // if no timeout, the DOM has not the time to update
                this.mediaUploadInProgress = false
            }, 500)

            medias.push(cacheKey)

            if(window.navigator.onLine && !this.forceUploadInCache) {
                const fileName = cacheKey.split(':')
                if(fileName) {
                    const mediaTransferHelper = MediaTransferHelper.getInstance()
                    const newMedia = await mediaTransferHelper.repeatFindingMediaIdFromFileName(cacheKey.split(':')[1])

                    if(newMedia?.id) {
                        mediaTransferHelper.saveMappingCacheKeyMediaId(cacheKey, newMedia.id)
                    }

                    this.$imageTransferHelper.addImageToReporterSubAlbum(newMedia.id)
                    this.$emit('addMedia', {cacheKey: cacheKey, newMediaId: newMedia.id})
                    return
                }
            }

            this.$emit('addMedia', { cacheKey: cacheKey })
        },
    },
})
